import Highcharts, { SeriesOptionsType } from "highcharts";
import { Dictionary, groupBy, isEqual, sum, uniq } from "lodash";
import { useEffect, useState } from "react";
import StoneXChart from "../../../../../StoneX/StoneXChart";
import { VarianceReportConfiguration, VarianceReportRow, VolumeSeriesOption } from "./Types";

interface VolumeChartProps {
  configuration: VarianceReportConfiguration;
  rows: VarianceReportRow[];
  pathToChart: string[] | undefined;
}

export function VolumeChart(props: VolumeChartProps) {

    const { configuration, rows, pathToChart } = props;

    const [chartOptions, setChartOptions] = useState<Highcharts.Options>({});
    const [rowsToChart, setRowsToChart] = useState<VarianceReportRow[]>(rows);

    const gray4 = getComputedStyle(document.documentElement).getPropertyValue('--gray4').trim();

    const seriesSettings = configuration.chart?.volumeValueChart?.series;
    const isSeriesEnabled = (option: VolumeSeriesOption) => seriesSettings?.[option]?.enabled ?? true;
    const seriesColor = (option: VolumeSeriesOption, fallback: string) => seriesSettings?.[option]?.color || fallback;
    const seriesOpacity = (option: VolumeSeriesOption) => seriesSettings?.[option]?.opacity || 1;

    function sumField(entries: VarianceReportRow[], selector: (row: VarianceReportRow) => number | undefined) {
        return sum(entries.map(row => selector(row) ?? 0));
    }

    function buildAllInSeries(groupedRows: Dictionary<VarianceReportRow[]>, name: string, stack: string): SeriesOptionsType {
        return {
            name,
            type: 'column',
            data: Object.entries(groupedRows).map(([, entries]) => sumField(entries, row => row.hedgedVolume)),
            color: seriesColor('allInHedgedVolume', '#4590DB'),
            opacity: seriesOpacity('allInHedgedVolume'),
            stack,
        } as SeriesOptionsType;
    }

    function buildBudgetSeries(groupedRows: Dictionary<VarianceReportRow[]>): SeriesOptionsType {
        return {
            name: 'Budget Volume',
            type: 'column',
            data: Object.entries(groupedRows).map(([, entries]) => sumField(entries, row => row.budgetVolume)),
            color: seriesColor('budgetVolume', '#39B6F2'),
            opacity: seriesOpacity('budgetVolume'),
            stack: 'budget',
        } as SeriesOptionsType;
    }

    function buildUnderlyingDeltaSeries(groupedRows: Dictionary<VarianceReportRow[]>): SeriesOptionsType {
        return {
            name: 'Underlying',
            type: 'column',
            data: Object.entries(groupedRows).map(([, entries]) => sumField(entries, row => row.underlyingHedgedVolume)),
            color: seriesColor('underlyingHedgedVolume', '#1327E1'),
            opacity: seriesOpacity('underlyingHedgedVolume'),
            stack: 'underlying',
        } as SeriesOptionsType;
    }

    function buildBasisDeltaSeries(groupedRows: Dictionary<VarianceReportRow[]>): SeriesOptionsType {
        return {
            name: 'Basis',
            type: 'column',
            data: Object.entries(groupedRows).map(([, entries]) => sumField(entries, row => row.basisHedgedVolume)),
            color: seriesColor('basisHedgedVolume', '#cc9502'),
            opacity: seriesOpacity('basisHedgedVolume'),
            stack: 'basis',
        } as SeriesOptionsType;
    }

    function buildSeries(): SeriesOptionsType[] {
        const groupedRows = groupBy(rowsToChart, x => x.budgetDate?.label!);
        const allInEnabled = isSeriesEnabled('allInHedgedVolume');
        const series: SeriesOptionsType[] = [];

        if (isSeriesEnabled('budgetVolume')) {
            series.push(buildBudgetSeries(groupedRows));
        }

        if (isSeriesEnabled('underlyingHedgedVolume')) {
            if (allInEnabled) {
                series.push(buildAllInSeries(groupedRows, 'Underlying (All In)', 'underlying'));
            }
            series.push(buildUnderlyingDeltaSeries(groupedRows));
        }

        if (isSeriesEnabled('basisHedgedVolume')) {
            if (allInEnabled) {
                series.push(buildAllInSeries(groupedRows, 'Basis (All In)', 'basis'));
            }
            series.push(buildBasisDeltaSeries(groupedRows));
        }

        if (allInEnabled) {
            series.push(buildAllInSeries(groupedRows, 'All-In Hedged Volume', 'allIn'));
        }

        return series;
    }

    function updateChart() {

        const isSameCommodity = uniq(rowsToChart.map(x => x.commodity?.commodityId)).length == 1;
        const commodity = isSameCommodity ? rowsToChart?.[0]?.commodity : undefined;
        const unitLabel = commodity?.displayUnit ?? '';

        const formattedVolume = (value?: number | null) => {
            if (value == null) {
                return '';
            }
            const formatted = Math.abs(value).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
            return unitLabel ? `${formatted} ${unitLabel}` : formatted;
        };

        setChartOptions({
            title: {
                text: 'Volume'
            },
            credits: {
                enabled: false,
            },
            tooltip: {
                useHTML: true,
                formatter: function() {
                    return `
                        <div style="margin: 10px; font-weight: 700; line-height: 1.5em">
                            ${this.x}
                            <br/>
                            ${this.series.name}: ${formattedVolume(this.point.y)}
                        </div>
                    `;
                }
            },
            plotOptions: {
                column: {
                    stacking: 'normal',
                },
                series: {
                    states: {
                        inactive: {
                            opacity: 1
                        }
                    }
                }
            },
            xAxis: {
                categories: getMonths(),
                labels: {
                    style: {
                        color: gray4,
                        fontWeight: '700'
                    }
                }
            },
            yAxis: [{
                allowDecimals: false,
                title: {
                    text: unitLabel ? `Volume (${unitLabel})` : 'Volume',
                    style: {
                        color: gray4,
                        fontWeight: '700'
                    }
                },
                labels: {
                    style: {
                        color: gray4,
                        fontWeight: '700'
                    }
                }
            }],
            series: buildSeries()
        });
    }

    function getMonths() {
        const allMonths = rows.filter(x => !x.isAggregated).map(x => x.budgetDate?.label!);
        return uniq(allMonths);
    }

    function updateRowsToChart() {
        let filteredRows = rows.filter(x => !x.isAggregated);

        if (pathToChart) {
            const pathLength = pathToChart.length;
            filteredRows = filteredRows.filter(x => isEqual(x.path.slice(0, pathLength), pathToChart));
        }

        setRowsToChart(filteredRows);
    }

    useEffect(updateRowsToChart, [rows, pathToChart]);
    useEffect(updateChart, [rowsToChart, configuration]);

    const isSameCommodity = uniq(rowsToChart.map(x => x.commodity?.commodityId)).length == 1;
    if (!isSameCommodity) {
        return null;
    }

    return <StoneXChart height='700px' highcharts={Highcharts} options={chartOptions} />;
}
